"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useTable = void 0;
const react_1 = require("react");
const lodash_1 = require("lodash");
const event_callback_hook_1 = require("../../utils/event-callback-hook");
const force_render_1 = require("../../utils/force-render");
const named_settings_context_1 = require("../../common/settings/named/named-settings-context");
const export_dialog_1 = require("../../modules/export/components/dialog/export-dialog/export-dialog");
const table_searchbar_1 = require("./table-searchbar");
const table_toolbar_1 = require("./table-toolbar");
const table_column_dialog_1 = require("./table-column-dialog");
const table_header_1 = require("./table-header");
const table_row_1 = require("./table-row");
const table_columns_hook_1 = require("./hooks/table-columns-hook");
const table_sort_hook_1 = require("./hooks/table-sort-hook");
const table_select_hook_1 = require("./hooks/table-select-hook");
const table_filters_hook_1 = require("./hooks/table-filters-hook");
const table_filter_dialog_1 = require("./table-filter-dialog");
const table_search_hook_1 = require("./hooks/table-search-hook");
const table_data_hook_1 = require("./hooks/table-data-hook");
const table_export_hook_1 = require("./hooks/table-export-hook");
const table_styles_1 = require("./table-styles");
function useTable(options, ref) {
    const namedSettingsContext = react_1.useContext(named_settings_context_1.NamedSettingsContext);
    const props = {
        tableId: '',
        version: 0,
        height: 10 * 30,
        disabled: false,
        tableName: null,
        defaultSorts: react_1.useMemo(() => [{ field: 'id', datakey: 'id', order: 'ASC', type: 'FIELD' }], []),
        defaultPreFilters: [],
        showRefreshButton: true,
        showColumnButton: true,
        showFilterButton: true,
        showNamedSettingsButton: namedSettingsContext.defaultTableNamedSettings,
        showBulkActionButton: true,
        showReportButton: true,
        showSelectBox: true,
        showSearchbar: true,
        showResetSortsButton: true,
        SearchbarComponent: table_searchbar_1.TableSearchbar,
        ToolbarComponent: table_toolbar_1.TableToolbar,
        HeaderComponent: table_header_1.TableHeader,
        RowComponent: table_row_1.TableRow,
        ColumnDialogComponent: table_column_dialog_1.TableColumnDialog,
        FilterDialogComponent: table_filter_dialog_1.TableFilterDialog,
        ExportDialogComponent: export_dialog_1.ExportDialog,
        exportProvideData: lodash_1.stubObject,
        bulkActions: react_1.useMemo(() => [], []),
        reportTag: null,
        include: react_1.useMemo(() => [], []),
        onActiveChange: lodash_1.noop,
        toolbarProps: {},
        useStyles: table_styles_1.useStyles,
        createRowStyle: react_1.useMemo(() => () => ({}), []),
        ...options,
    };
    const loaderRef = react_1.useRef(null);
    const listRef = react_1.useRef(null);
    const { forceRender } = force_render_1.useForceRender();
    const { columnsState, defaultColumnsState, filteredColumns, setColumnsState, columnDialogRef, openColumnDialog, closeColumnDialog, } = table_columns_hook_1.useTableColumns({
        tableId: props.tableId,
        version: props.version,
        columns: props.columns,
    });
    const { filterDialogRef, openFilterDialog, closeFilterDialog, filters, filtersState, setFiltersState, preFilters, setPreFilters, } = table_filters_hook_1.useTableFilters({
        tableId: props.tableId,
        version: props.version,
        columns: props.columns,
        initPreFilters: props.defaultPreFilters,
    });
    const { exportDialogRef, openExportDialog, closeExportDialog, } = table_export_hook_1.useTableExports();
    const { sorts, toggleSortColumn, resetSorts, setSorts } = table_sort_hook_1.useTableSort({
        tableId: props.tableId,
        version: props.version,
        columns: props.columns,
        defaultSorts: props.defaultSorts,
    });
    const { searchQuery, setSearchQuery } = table_search_hook_1.useTableSearch({
        tableId: props.tableId,
        version: props.version,
    });
    const { selected, activeRow, toggleAllRowSelection, toggleRowSelection, resetSelection, setActiveRow, setNextRowActive, setPrevRowActive, } = table_select_hook_1.useTableSelect({
        source: props.source,
        listRef,
        onActiveChange: props.onActiveChange,
    });
    table_data_hook_1.useTableData({
        source: props.source,
        searchQuery,
        sorts,
        preFilters,
        filters,
        filtersState,
        loaderRef,
        include: props.include,
    });
    /**
     * Refreshes table source and infinite loader cache.
     */
    const refresh = event_callback_hook_1.useEventCallback(() => {
        props.source.reset();
        forceRender();
        requestAnimationFrame(() => {
            var _a;
            (_a = loaderRef.current) === null || _a === void 0 ? void 0 : _a.resetloadMoreItemsCache(true);
        });
    });
    const handleKeyNavigation = event_callback_hook_1.useEventCallback((e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault(); // prevent default to prevent unwanted scrolling
            setNextRowActive();
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault(); // prevent default to prevent unwanted scrolling
            setPrevRowActive();
        }
    });
    const useTableStyles = props.useStyles || table_styles_1.useStyles;
    const classes = useTableStyles();
    const context = react_1.useMemo(() => ({
        columns: props.columns,
        source: props.source,
        bulkActions: props.bulkActions,
        reportTag: props.reportTag,
        columnsState,
        disabled: props.disabled,
        disabledRefreshButton: false,
        disabledColumnButton: false,
        disabledFilterButton: false,
        disabledBulkActionButton: false,
        disabledReportButton: false,
        disabledResetSortsButton: false,
        disabledNamedSettingsButton: false,
        showRefreshButton: props.showRefreshButton,
        showColumnButton: props.showColumnButton,
        showFilterButton: props.showFilterButton,
        showNamedSettingsButton: props.showNamedSettingsButton,
        showBulkActionButton: props.showBulkActionButton,
        showReportButton: props.showReportButton,
        showSelectBox: props.showSelectBox,
        showResetSortsButton: props.showResetSortsButton,
        tableName: props.tableName,
        totalCount: props.source.count,
        loadedCount: props.source.items.length,
        filteredColumns,
        searchQuery,
        sorts,
        filters,
        filtersState,
        preFilters,
        setPreFilters,
        toggleAllRowSelection,
        toggleRowSelection,
        resetSelection,
        activeRow,
        setActiveRow,
        setPrevRowActive,
        setNextRowActive,
        refresh,
        setColumnsState,
        defaultColumnsState,
        openColumnDialog,
        closeColumnDialog,
        openFilterDialog,
        closeFilterDialog,
        openExportDialog,
        closeExportDialog,
        toggleSortColumn,
        setSearchQuery,
        setFiltersState,
        resetSorts,
        setSorts,
        classes,
    }), [
        props.columns,
        props.source,
        props.bulkActions,
        props.reportTag,
        props.disabled,
        props.showRefreshButton,
        props.showColumnButton,
        props.showFilterButton,
        props.showNamedSettingsButton,
        props.showBulkActionButton,
        props.showReportButton,
        props.showSelectBox,
        props.showResetSortsButton,
        props.tableName,
        columnsState,
        filteredColumns,
        searchQuery,
        sorts,
        filters,
        filtersState,
        preFilters,
        setPreFilters,
        toggleAllRowSelection,
        toggleRowSelection,
        resetSelection,
        activeRow,
        setActiveRow,
        setPrevRowActive,
        setNextRowActive,
        refresh,
        setColumnsState,
        defaultColumnsState,
        openColumnDialog,
        closeColumnDialog,
        openFilterDialog,
        closeFilterDialog,
        openExportDialog,
        closeExportDialog,
        toggleSortColumn,
        setSearchQuery,
        setFiltersState,
        resetSorts,
        setSorts,
        classes,
    ]);
    react_1.useImperativeHandle(ref, () => context, [context]);
    const selectedContext = react_1.useMemo(() => ({
        selected,
        activeRow,
    }), [selected, activeRow]);
    return {
        props,
        context,
        selectedContext,
        columnDialogRef,
        loaderRef,
        listRef,
        filterDialogRef,
        exportDialogRef,
        handleKeyNavigation,
    };
}
exports.useTable = useTable;
//# sourceMappingURL=table-hook.js.map